import AtriumApi from "api";
import DarkButton from "components/actions/DarkButton";
import TransparentButton from "components/actions/TransparentButton";
import AlertPane from "components/alert";
import Avatar from "components/avatar";
import DatePicker from "components/fields/DatePicker";
import InputField from "components/fields/InputField";
import Select from "components/fields/Select";
import UploadIcon from "components/icons/UploadIcon";
import StatusLabel from "components/label/StatusLabel";
import { Form, Formik } from "formik";
import { useAuth } from "hooks/useAuth";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import * as Yup from "yup";

const ProfileDetails = () => {
  const auth = useAuth();
  const currentUser = auth.user || {};
  const [notice, setNotice] = useState("");
  const [user, setUser] = useState({
    email: currentUser.email || "",
    phone: currentUser.phone || "",
    firstName: currentUser.firstName || "",
    lastName: currentUser.lastName || "",
    dob: currentUser.dob || "",
    address: currentUser.address || "",
    city: currentUser.city || "",
    state: currentUser.state || "",
    country: currentUser.country || "",
    zip: currentUser.zip || "",
    familyRelationship: currentUser.familyRelationship || "",
  });
  const [file, setFile] = useState();
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [locationNotice, setLocationNotice] = useState("");
  const [locationErrors, setLocationErrors] = useState([]);

  const getProfileDetails = async () => {
    AtriumApi.get("/v1/users/get-profile").then((resp) => {
      auth.setUser(resp?.data?.user);
    });
  };

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required("First name is required"),
    lastName: Yup.string().required("Last name is required"),
    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),
    phone: Yup.string()
      .matches(/^[0-9\s\-()+]*$/, "Phone number should only contain numbers.")
      .required("Phone number is required"),
    familyRelationship: Yup.string().required(
      "Family relationship is required"
    ),
    dob: Yup.date().nullable(), // or Yup.date().optional()

    // dob: Yup.string().required("Date of birth is required"),
  });
  // const removeAvatar = (e) => {
  //   e.target.setAttribute("disabled", "");
  //   setFile(undefined);
  //   setUploading(true);
  //   const formData = new FormData();

  //   formData.append("avatar", null);

  //   AtriumApi.patch(`/v1/users/update/${currentUser?.id}`, formData, {
  //     headers: {
  //       "Content-Type": "multipart/form-data",
  //     },
  //   }).then((response) => {
  //     // auth.login(response.data.token);
  //     getProfileDetails();
  //     e.target.removeAttribute("disabled");
  //     setUploading(false);
  //   });
  // };

  const handleFileChange = async (e) => {
    if (e.target.files) {
      e.target.setAttribute("disabled", "");
      setUploading(true);
      setFile(e.target.files[0]);

      const formData = new FormData();
      formData.append("avatar", e.target.files[0]);

      try {
        await AtriumApi.patch(`/v1/users/update/${currentUser?.id}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        toast.success("Avatar updated successfully.");
        // auth.login(response.data.token); // Uncomment if needed
        getProfileDetails();
      } catch (error) {
        toast.error(error?.data?.message || "An error occurred");
        // Optional: Show toast or alert to user
      } finally {
        e.target.removeAttribute("disabled");
        setUploading(false);
        setFile(undefined);
      }
    }
  };

  const onSaveDetails = (values) => {
    const formElement = document.querySelector("form");
    formElement.classList.add("animate-pulse");

    const submitButton = formElement.querySelector('button[type="submit"]');
    submitButton.setAttribute("disabled", "");

    let { email, phone, dob, firstName, lastName, familyRelationship } = values;
    if (familyRelationship === "") familyRelationship = null;
    const formData = new FormData();

    formData.append("email", email);
    formData.append("phone", phone);
    formData.append("dob", dob);
    formData.append("firstName", firstName);
    formData.append("lastName", lastName);
    formData.append("familyRelationship", familyRelationship);

    AtriumApi.patch(`/v1/users/update/${currentUser?.id}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
      .then((response) => {
        formElement.classList.remove("animate-pulse");
        submitButton.removeAttribute("disabled");
        if (response.status === 422) {
          setErrors(response.data.errors);
        } else if (response.status === 200) {
          // auth.login(response.data.token);
          getProfileDetails();
          setNotice("Your changes are saved successfully");
        } else {
          throw new Error("unexpected response");
        }
      })
      .catch((error) => {
        toast.error(error?.data?.message || "Something went wrong");
      })
      .finally(() => {
        formElement.classList.remove("animate-pulse");
        submitButton.removeAttribute("disabled");
      });
  };

  const onSaveLocation = (e) => {
    e.preventDefault();
    setLocationErrors([]);
    setLocationNotice("");
    e.target.classList.add("animate-pulse");
    const submitButton = e.target.querySelector('button[type="submit"]');
    submitButton.setAttribute("disabled", "");
    const { address, city, state, country, zip } = user;

    const formData = new FormData();

    formData.append("address", address);
    formData.append("city", city);
    formData.append("state", state);
    formData.append("country", country);
    formData.append("zip", zip);

    AtriumApi.patch(`/v1/users/update/${currentUser?.id}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }).then((response) => {
      e.target.classList.remove("animate-pulse");
      submitButton.removeAttribute("disabled");
      if (response.status === 422) {
        setLocationErrors(response.data.errors);
      } else if (response.status === 200) {
        // auth.login(response.data.token);
        getProfileDetails();
        setLocationNotice("Your changes are saved successfully");
      } else {
        throw new Error("unexpected response");
      }
    });
  };

  useEffect(() => {
    getProfileDetails();
  }, []);

  if (!auth.user) return <></>;

  // change date as per mac MM/DD/YYYY
  function formatToMMDDYYYY(date) {
    if (!date) return "";
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  }

  return (
    <>
      <div className="w-full flex flex-row justify-center md:justify-end relative items-center rounded-[30px] p-3 md:p-[30px] gap-[30px] shadow-atrium shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none mb-5 flex-wrap ">
        <div className={`${uploading ? "spin-border" : ""}`}>
          {file ? (
            <img
              src={URL.createObjectURL(file)}
              alt={`${user.firstName} ${user.lastName}`}
              className="h-[72px] w-[72px] rounded-full m-auto"
            />
          ) : (
            <Avatar user={auth?.user} />
          )}
        </div>

        <div className="flex-1">
          <p className="font-bold text-xl mb-2 whitespace-nowrap">
            {auth.user.firstName} {auth.user.lastName}
          </p>
          <StatusLabel status={auth.user.role} />
        </div>

        {auth.user.avatar?.url ? (
          <div className="flex center gap-3">
            <DarkButton>
              Update image{" "}
              <input
                className="block absolute left-0 top-0 w-full h-full z-[1] bg-gray-800 rounded-2xl opacity-0 cursor-pointer"
                type="file"
                onChange={handleFileChange}
                accept="image/*"
              />
            </DarkButton>
            {/* <TransparentButton onClick={removeAvatar}>
              Remove image
            </TransparentButton> */}
          </div>
        ) : (
          <TransparentButton>
            <span className="inline-block align-middle mr-2 text-gray-600">
              <UploadIcon />
            </span>
            <span className="inline-block align-middle text-gray-600">
              Upload profile picture
            </span>
            <input
              className="block absolute left-0 top-0 w-full h-full z-[1] bg-gray-800 rounded-2xl opacity-0 cursor-pointer"
              type="file"
              onChange={handleFileChange}
              accept="image/*"
            />
          </TransparentButton>
        )}
      </div>

      <Formik
        enableReinitialize
        initialValues={user}
        validationSchema={validationSchema}
        onSubmit={onSaveDetails}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
        }) => (
          <Form
            className="w-full block md:flex flex-row p-3 md:p-[30px] relative items-start rounded-[30px] gap-[30px] shadow-atrium shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none mb-5"
            onSubmit={(e) => {
              e.preventDefault(); // Stop default form behavior
              handleSubmit(); // Call Formik's submit
            }}
            noValidate
          >
            <div className="flex-1 mb-6">
              <h2 className="font-bold text-lg mb-2">Personal Details</h2>
              <p className="text-gray-700 text-sm">
                Update your personal information
              </p>
            </div>

            <div className="flex-2">
              {notice && <AlertPane state="success">{notice}</AlertPane>}
              {errors.length ? (
                <AlertPane state="error">
                  Sorry, we have trouble to save your changes
                </AlertPane>
              ) : (
                ""
              )}
              <div className="flex flex-col md:flex-row gap-3 md:gap-6 pb-4">
                <InputField
                  variant="profile_form"
                  label="Email address"
                  placeholder="Enter your email address"
                  name="email"
                  type="text"
                  extra="flex-1 flex flex-col"
                  value={values.email}
                  error={errors.email}
                  touched={touched.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <InputField
                  variant="profile_form"
                  label="Phone number"
                  placeholder="Enter your phone number"
                  name="phone"
                  type="text"
                  extra="flex-1 flex flex-col"
                  value={values.phone}
                  error={errors.phone}
                  touched={touched.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>

              <div className="flex flex-col md:flex-row gap-3 md:gap-6 pb-4">
                <InputField
                  variant="profile_form"
                  label="First name"
                  placeholder="Enter your first name"
                  name="firstName"
                  type="text"
                  extra="flex-1 flex flex-col"
                  value={values.firstName}
                  error={errors.firstName}
                  touched={touched.firstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <InputField
                  variant="profile_form"
                  label="Last name"
                  placeholder="Enter your last name"
                  name="lastName"
                  type="text"
                  extra="flex-1 flex flex-col"
                  value={values.lastName}
                  error={errors.lastName}
                  touched={touched.lastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>

              <div className="flex flex-col md:flex-row gap-3 md:gap-6 pb-4">
                <div className="flex-1 flex flex-col">
                  {/* <DatePicker
                    name="dob"
                    isFutureDateDisabled
                    label="Date of birth"
                    height={30}
                    
                    value={
                      (values.dob &&
                        new Date(values.dob).toLocaleDateString(["en-US"])) ||
                      ""
                    }
                    onChange={(v) =>
                      setFieldValue("dob", v.toLocaleDateString(["en-US"]))
                    }
                    onBlur={handleBlur}
                  /> */}
                  <DatePicker
                    name="dob"
                    isFutureDateDisabled
                    label="Date of birth"
                    height={30}
                    value={formatToMMDDYYYY(values.dob)}
                    onChange={(v) => setFieldValue("dob", formatToMMDDYYYY(v))}
                    onBlur={handleBlur}
                  />
                </div>
                <div className="flex-1 flex flex-col">
                  <label
                    className="text-sm text-navy-700 dark:text-white ml-2 mb-2"
                    htmlFor="family-relationship"
                  >
                    Family relationship
                  </label>
                  <Select
                    name="familyRelationship"
                    placeholder="Select your family relationship"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.familyRelationship}
                    error={errors.familyRelationship}
                    touched={touched.familyRelationship}
                    style={{
                      borderColor: touched.status && errors.status ? "red" : "",
                    }}
                    required
                  >
                    <option value="" disabled hidden></option>
                    <option value="primary">Primary</option>
                    <option value="married">Married</option>
                    <option value="child">Child</option>
                  </Select>
                  {touched.familyRelationship && errors.familyRelationship && (
                    <p className="text-red-500 text-xs mt-1 ml-2">
                      {errors.familyRelationship}
                    </p>
                  )}
                </div>
              </div>

              <DarkButton type="submit">Save changes</DarkButton>
            </div>
          </Form>
        )}
      </Formik>

      <form
        onSubmit={onSaveLocation}
        className="w-full block md:flex flex-row p-3 md:p-[30px] relative items-start rounded-[30px] gap-[30px] shadow-atrium shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none mb-5"
      >
        <div className="flex-1 mb-6">
          <h2 className="font-bold text-lg mb-2">Location</h2>
          <p className="text-gray-700 text-sm">Update your location</p>
        </div>

        <div className="flex-2">
          {locationNotice && (
            <AlertPane state="success">{locationNotice}</AlertPane>
          )}
          {locationErrors.length ? (
            <AlertPane state="error">
              Sorry, we have trouble to save your changes
            </AlertPane>
          ) : (
            ""
          )}
          <div className="flex flex-col md:flex-row gap-3 md:gap-6 pb-4">
            <InputField
              variant="profile_form"
              label="Address"
              placeholder="Enter your address"
              id="address"
              type="text"
              extra="flex-1 flex flex-col"
              value={user.address || ""}
              state={
                locationErrors.findIndex(
                  (error) => error.path === "address"
                ) !== -1
                  ? "error"
                  : ""
              }
              onChange={(e) => setUser({ ...user, address: e.target.value })}
            />
            <InputField
              variant="profile_form"
              label="City"
              placeholder="Enter your city"
              id="city"
              type="text"
              extra="flex-1 flex flex-col"
              value={user.city || ""}
              state={
                locationErrors.findIndex((error) => error.path === "city") !==
                -1
                  ? "error"
                  : ""
              }
              onChange={(e) => setUser({ ...user, city: e.target.value })}
            />
          </div>

          <div className="flex flex-col md:flex-row gap-3 md:gap-6 pb-4">
            <InputField
              variant="profile_form"
              label="State"
              placeholder="Enter your state"
              id="state"
              type="text"
              extra="flex-1 flex flex-col"
              state={
                locationErrors.findIndex((error) => error.path === "state") !==
                -1
                  ? "error"
                  : ""
              }
              value={user.state || ""}
              onChange={(e) => setUser({ ...user, state: e.target.value })}
            />
            <InputField
              variant="profile_form"
              label="Zip code"
              placeholder="Enter your zip code"
              id="zip-code"
              type="text"
              extra="flex-1 flex flex-col"
              value={user.zip || ""}
              state={
                locationErrors.findIndex((error) => error.path === "zip") !== -1
                  ? "error"
                  : ""
              }
              onChange={(e) => setUser({ ...user, zip: e.target.value })}
            />
          </div>

          <DarkButton type="submit">Save changes</DarkButton>
        </div>
      </form>
    </>
  );
};

export default ProfileDetails;
